import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import ImageUploadService from '../../../services/imageUploadService';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/ui/Toast';
import { friendlyError } from '../../../utils/errorMessages';
import ScheduleModal from '../../../components/ui/ScheduleModal';

const PLATFORM_CONFIG = {
  linkedin: { label: 'LinkedIn', icon: 'Linkedin', color: '#0A66C2', maxChars: 3000, maxImages: 20, maxSizeMB: 5, acceptTypes: 'image/jpeg,image/png' },
  twitter: { label: 'X', icon: 'X', color: '#FFFFFF', maxChars: 280, maxImages: 4, maxSizeMB: 5, acceptTypes: 'image/jpeg,image/png,image/gif,image/webp' }
};

// Convert markdown bold (**text**) to Unicode bold for LinkedIn
const markdownToLinkedIn = (text) => {
  if (!text) return text;
  return text.replace(/\*\*(.*?)\*\*/g, (_, inner) => {
    return inner.split('').map(ch => {
      const code = ch.charCodeAt(0);
      if (code >= 65 && code <= 90) return String.fromCodePoint(0x1D5D4 + (code - 65));
      if (code >= 97 && code <= 122) return String.fromCodePoint(0x1D5EE + (code - 97));
      if (code >= 48 && code <= 57) return String.fromCodePoint(0x1D7EC + (code - 48));
      return ch;
    }).join('');
  });
};

const PlatformTab = ({
  platform,
  content,
  onChange,
  uploadedImages = [],
  imagePreviewUrls = [],
  tokens,
  tokenStatus,
  contentPostId,
  saveContentToDashboard,
  onImagesUploaded,
  onImagePreviewAdded,
  onImageRemoved,
  publishing,
  published,
  publishedPostId,
  onPublish,
  locked
}) => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const config = PLATFORM_CONFIG[platform];
  const charCount = content?.length || 0;
  const isOverLimit = charCount > config.maxChars;
  const isNearLimit = charCount > config.maxChars * 0.9;
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User';
  const displayTitle = userProfile?.bio || 'Content Creator';
  // Try multiple sources for avatar
  const displayAvatar = userProfile?.avatar_url || user?.user_metadata?.avatar_url || user?.user_metadata?.picture || '';
  const isConnected = tokenStatus?.[platform] === 'valid';
  const previewContent = platform === 'linkedin' ? markdownToLinkedIn(content) : content;

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(150, textareaRef.current.scrollHeight) + 'px';
    }
  }, [content]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Simple validation — check size and basic type
    const validFiles = [];
    const errors = [];
    const maxSize = config.maxSizeMB * 1024 * 1024;

    for (const file of files) {
      if (file.size > maxSize) {
        errors.push(`${file.name}: exceeds ${config.maxSizeMB}MB limit`);
      } else if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
        errors.push(`${file.name}: unsupported format (${file.type || 'unknown'})`);
      } else {
        validFiles.push(file);
      }
    }

    if (errors.length > 0) toast.error(errors.join('\n'));
    if (validFiles.length === 0) return;

    if ((uploadedImages.length + validFiles.length) > config.maxImages) {
      toast.error(`${config.label} allows max ${config.maxImages} images per post`);
      return;
    }

    // Generate preview URLs immediately
    const previews = validFiles.map(f => URL.createObjectURL(f));
    onImagePreviewAdded(platform, previews);

    // Upload to platform
    setUploading(true);
    try {
      let postId = contentPostId;
      if (!postId) postId = await saveContentToDashboard();
      const accessToken = tokens?.[platform]?.token;
      if (!accessToken) throw new Error(`Connect ${config.label} to upload images`);

      const uploadResult = await ImageUploadService.uploadMultipleToPlatform(
        validFiles, platform, accessToken, userProfile?.id, postId
      );

      if (uploadResult.successful.length > 0) {
        onImagesUploaded(platform, uploadResult.successful.map(s => s.result));
        toast.success(`${uploadResult.successful.length} image${uploadResult.successful.length > 1 ? 's' : ''} uploaded`);
      }
      if (uploadResult.failed.length > 0) {
        // Remove preview URLs for failed uploads so they don't linger in the UI
        const failCount = uploadResult.failed.length;
        const currentPreviews = imagePreviewUrls || [];
        if (failCount > 0 && currentPreviews.length > 0) {
          // Failed images are at the end of the preview list (added in order)
          const successCount = uploadResult.successful.length;
          const keepCount = currentPreviews.length - failCount;
          // Rebuild previews: keep only the ones that correspond to successful uploads
          onImagePreviewAdded(platform, currentPreviews.slice(0, Math.max(0, keepCount)), true);
        }
        toast.error(`${failCount} failed: ${uploadResult.failed.map(f => f.error).join(', ')}`);
      }
    } catch (error) {
      // Upload completely failed — clear all previews that were just added
      onImagePreviewAdded(platform, imagePreviewUrls?.slice(0, uploadedImages.length) || [], true);
      toast.error(friendlyError(error, 'Upload failed.'));
    } finally {
      setUploading(false);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Published state — show success with link
  if (published) {
    const postUrl = platform === 'linkedin'
      ? (publishedPostId ? `https://www.linkedin.com/feed/update/${publishedPostId}` : 'https://www.linkedin.com/feed/')
      : (publishedPostId ? `https://x.com/i/status/${publishedPostId}` : 'https://x.com');

    return (
      <div className="bg-card rounded-xl border border-emerald-900/50 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-emerald-900/30 bg-emerald-950/20">
          <div className="flex items-center gap-2">
            <Icon name={config.icon} size={14} color={config.color} />
            <span className="text-sm font-medium text-foreground">{config.label}</span>
          </div>
          <span className="flex items-center gap-1.5 text-xs text-emerald-400">
            <Icon name="CheckCircle" size={13} />
            Published
          </span>
        </div>
        <div className="p-6 flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-emerald-900/30 flex items-center justify-center">
            <Icon name="CheckCircle" size={24} className="text-emerald-400" />
          </div>
          <p className="text-sm text-foreground">Successfully published to {config.label}</p>
          <a
            href={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted text-foreground text-sm font-medium rounded-lg transition-colors"
          >
            <Icon name="ExternalLink" size={14} />
            View on {config.label}
          </a>
        </div>
      </div>
    );
  }

  const allImages = imagePreviewUrls.length > 0 ? imagePreviewUrls : uploadedImages;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Editor Side */}
      <div className="bg-card rounded-xl border border-border overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name={config.icon} size={14} color={config.color} />
            <span className="text-xs font-medium text-foreground">Edit</span>
          </div>
          <span className={`text-xs font-mono ${isOverLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-muted-foreground'}`}>
            {charCount}/{config.maxChars}
          </span>
        </div>

        {/* Text editor */}
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => onChange(e.target.value)}
          disabled={locked}
          className={`flex-1 w-full px-4 py-3 bg-transparent text-foreground placeholder:text-muted-foreground text-sm leading-relaxed resize-none focus:outline-none min-h-[150px] ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
          style={{ fontFamily: "'Source Sans 3', sans-serif" }}
        />

        {/* Bottom toolbar with image icon — clearly visible at bottom-left */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border">
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !isConnected || locked}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-all disabled:opacity-40"
              title={!isConnected ? `Connect ${config.label} first` : `Attach images (max ${config.maxImages})`}
            >
              <Icon name="ImagePlus" size={16} />
              {uploading ? (
                <><Icon name="Loader" size={12} className="animate-spin" /> Uploading...</>
              ) : (
                <span>{allImages.length > 0 ? `${allImages.length} attached` : 'Add images'}</span>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={config.acceptTypes}
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      </div>

      {/* Preview Side — with shimmer border */}
      <div className="preview-glow"><div className="bg-card rounded-[12.5px] flex flex-col overflow-hidden border border-border">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name={config.icon} size={14} color={config.color} />
            <span className="text-xs font-medium text-foreground">Preview</span>
          </div>
          {isConnected ? (
            <button
              onClick={() => onPublish(platform)}
              disabled={publishing || !content?.trim() || locked}
              className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg transition-all ${
                publishing ? 'bg-muted text-muted-foreground cursor-wait' : 'bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white hover:opacity-90'
              }`}
            >
              <Icon name={publishing ? 'Loader' : 'Send'} size={12} />
              {publishing ? 'Posting...' : `Post to ${config.label}`}
            </button>
          ) : (
            <a href="/o-auth-integration-setup" className="text-xs text-muted-foreground hover:text-foreground underline">
              Connect {config.label}
            </a>
          )}
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {/* Author */}
          <div className="flex items-start gap-3 mb-3">
            {displayAvatar ? (
              <Image src={displayAvatar} alt={displayName} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <Icon name="User" size={16} className="text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-foreground truncate">{displayName}</h4>
              <p className="text-xs text-muted-foreground line-clamp-1">{displayTitle}</p>
            </div>
          </div>

          {/* Content */}
          <div className={`text-sm ${platform === 'linkedin' ? 'leading-relaxed whitespace-pre-wrap' : 'leading-normal'} text-foreground`}>
            {content ? (
              platform === 'linkedin' ? (
                <span>{previewContent}</span>
              ) : (
                <div className="markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                </div>
              )
            ) : (
              <p className="text-muted-foreground italic">Preview will appear here...</p>
            )}
          </div>

          {/* Images */}
          {allImages.length > 0 && (
            <div className={`mt-3 rounded-lg overflow-hidden border border-border ${
              allImages.length === 1 ? '' : 'grid grid-cols-2 gap-0.5'
            }`}>
              {allImages.map((item, idx) => {
                const isUrl = typeof item === 'string';
                return (
                  <div key={idx} className={`relative group bg-muted flex items-center justify-center overflow-hidden ${
                    allImages.length === 1 ? 'h-48' : 'h-32'
                  }`}>
                    {isUrl ? (
                      <img src={item} alt={`Attachment ${idx + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-muted-foreground">
                        <Icon name="ImagePlus" size={18} />
                        <span className="text-xs">Image {idx + 1}</span>
                      </div>
                    )}
                    {!published && (
                      <button
                        onClick={() => onImageRemoved(platform, idx)}
                        className="absolute top-1.5 right-1.5 p-1 bg-black/70 hover:bg-red-600 text-white/80 hover:text-white rounded-full opacity-0 group-hover:opacity-100 transition-all"
                        title="Remove image"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Engagement Mockup */}
          {platform === 'twitter' && (
            <div className="flex items-center gap-6 mt-3 pt-3 border-t border-border">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon name="Heart" size={13} /> 0</span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon name="MessageCircle" size={13} /> 0</span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon name="Repeat2" size={13} /> 0</span>
            </div>
          )}
          {platform === 'linkedin' && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon name="ThumbsUp" size={13} /> Like</span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon name="MessageCircle" size={13} /> Comment</span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon name="Repeat2" size={13} /> Repost</span>
            </div>
          )}
        </div>
      </div></div>
    </div>
  );
};

const EditPreviewModal = ({
  isOpen,
  onClose,
  linkedinContent,
  setLinkedinContent,
  twitterContent,
  setTwitterContent,
  selectedPlatforms,
  setSelectedPlatforms,
  tokenStatus,
  tokens,
  uploadedImages,
  imagePreviewUrls,
  onImagesUploaded,
  onImagePreviewAdded,
  onImageRemoved,
  contentPostId,
  saveContentToDashboard,
  publishing,
  publishedPlatforms,
  publishedPostIds,
  onPublish,
  onUnifiedPublish,
  onRegenerate,
  isRegenerating,
  regenDisabled,
  regenDisabledReason,
  onSchedule,
  scheduling,
  scheduledInfo
}) => {
  const [showRegenInput, setShowRegenInput] = useState(false);
  const [regenFeedback, setRegenFeedback] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  if (!isOpen) return null;

  const selectedCount = Object.values(selectedPlatforms).filter(Boolean).length;
  const allPublished = ['linkedin', 'twitter'].filter(p => selectedPlatforms[p]).every(p => publishedPlatforms?.[p]);
  const allConnected = ['linkedin', 'twitter'].filter(p => selectedPlatforms[p]).every(p => tokenStatus?.[p] === 'valid');
  const anySelected = selectedCount > 0;
  const isLocked = !!scheduledInfo;

  const handleRegenClick = () => {
    if (regenDisabled) return;
    setShowRegenInput(true);
  };

  const handleRegenSubmit = () => {
    onRegenerate(regenFeedback.trim());
    setRegenFeedback('');
    setShowRegenInput(false);
  };

  const handleRegenSkip = () => {
    onRegenerate('');
    setRegenFeedback('');
    setShowRegenInput(false);
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-6xl mx-4 max-h-[90vh] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Scheduled lock banner */}
        {isLocked && (
          <div className="px-6 py-3 border-b border-[#A78BFA]/30 bg-[#A78BFA]/10 flex items-center gap-2">
            <Icon name="Lock" size={14} className="text-[#A78BFA]" />
            <span className="text-sm text-foreground">
              Content locked — scheduled for{' '}
              {scheduledInfo.platforms && (
                <>
                  {[
                    scheduledInfo.platforms.linkedin && 'LinkedIn',
                    scheduledInfo.platforms.twitter && 'X',
                  ].filter(Boolean).join(' & ')}{' '}
                  on{' '}
                </>
              )}
              <strong>{new Date(scheduledInfo.scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}</strong>{' '}
              at <strong>{new Date(scheduledInfo.scheduledAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}</strong>
            </span>
            <span className="text-xs text-muted-foreground ml-auto">Cancel from Dashboard to make changes</span>
          </div>
        )}

        {/* Regenerate feedback input — slides down */}
        {showRegenInput && !isLocked && (
          <div className="px-6 py-4 border-b border-border bg-card/80">
            <p className="text-sm text-foreground mb-2">What should be improved?</p>
            <textarea
              value={regenFeedback}
              onChange={(e) => setRegenFeedback(e.target.value)}
              placeholder="e.g., Make it more concise, add a stronger hook, focus on the technical details..."
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-violet-500 resize-none"
              autoFocus
            />
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={handleRegenSubmit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white rounded-lg hover:opacity-90 transition-all"
              >
                <Icon name="RefreshCw" size={12} />
                Regenerate with feedback
              </button>
              <button
                onClick={handleRegenSkip}
                className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted rounded-lg transition-all"
              >
                Regenerate without
              </button>
              <button
                onClick={() => { setShowRegenInput(false); setRegenFeedback(''); }}
                className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-foreground">Edit & Preview</h2>
            <div className="flex items-center gap-2">
              {['linkedin', 'twitter'].map(p => {
                const cfg = PLATFORM_CONFIG[p];
                const selected = selectedPlatforms[p];
                const isPublished = publishedPlatforms?.[p];
                const connected = tokenStatus?.[p] === 'valid';
                return (
                  <button
                    key={p}
                    onClick={() => !isPublished && setSelectedPlatforms(prev => ({ ...prev, [p]: !prev[p] }))}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border-2 transition-all ${
                      isPublished ? 'border-emerald-600 bg-emerald-950/30 text-emerald-400 cursor-default' :
                      selected ? 'border-emerald-500 bg-emerald-500/10 text-foreground shadow-[0_0_0_1px_rgba(16,185,129,0.2)]' :
                      'border-border bg-card text-muted-foreground hover:border-muted-foreground'
                    }`}
                    title={isPublished ? `Published to ${cfg.label}` : selected ? `${cfg.label} selected — click to deselect` : `Click to select ${cfg.label}`}
                  >
                    {isPublished ? (
                      <Icon name="CheckCircle" size={13} />
                    ) : selected ? (
                      <Icon name="CircleCheck" size={13} className="text-emerald-400" />
                    ) : (
                      <Icon name="Circle" size={13} className="text-muted-foreground" />
                    )}
                    <Icon name={cfg.icon} size={12} color={selected || isPublished ? cfg.color : undefined} />
                    {cfg.label}
                    {!connected && !isPublished && (
                      <span className="text-[9px] text-amber-400 ml-0.5">!</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!allPublished && (
              <div className="relative group">
                <button
                  onClick={handleRegenClick}
                  disabled={isRegenerating || regenDisabled || isLocked}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all disabled:opacity-50 ${
                    regenDisabled
                      ? 'text-red-400 bg-red-500/10 border border-red-500/20 cursor-not-allowed'
                      : 'text-muted-foreground hover:text-foreground bg-muted hover:bg-muted'
                  }`}
                >
                  <Icon name={isRegenerating ? 'Loader' : 'RefreshCw'} size={13} className={isRegenerating ? 'animate-spin' : ''} />
                  {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                </button>
                {regenDisabled && regenDisabledReason && (
                  <div className="absolute right-0 top-full mt-1 w-56 px-3 py-2 bg-card border border-border rounded-lg text-xs text-foreground shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    {regenDisabledReason}
                  </div>
                )}
              </div>
            )}

            {/* Scheduled confirmation */}
            {scheduledInfo && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <Icon name="CheckCircle" size={13} />
                Scheduled for {new Date(scheduledInfo.scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} at {new Date(scheduledInfo.scheduledAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
              </div>
            )}

            {!allPublished && !scheduledInfo && anySelected && (
              <>
                {/* Schedule button */}
                <button
                  onClick={() => setShowScheduleModal(true)}
                  disabled={scheduling || publishing?.unified}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted rounded-lg transition-all disabled:opacity-50"
                  title="Schedule for later"
                >
                  <Icon name={scheduling ? 'Loader2' : 'Clock'} size={13} className={scheduling ? 'animate-spin' : ''} />
                  {scheduling ? 'Scheduling...' : 'Schedule'}
                </button>

                {/* Publish button */}
                <div className="relative group">
                  <button
                    onClick={allConnected ? onUnifiedPublish : undefined}
                    disabled={publishing?.unified || !allConnected || scheduling}
                    className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg transition-all disabled:opacity-50 ${
                      !allConnected
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white hover:opacity-90'
                    }`}
                  >
                    <Icon name={publishing?.unified ? 'Loader' : 'Send'} size={13} />
                    {publishing?.unified ? 'Publishing...' : `Post to ${selectedCount} Platform${selectedCount > 1 ? 's' : ''}`}
                  </button>
                  {!allConnected && (
                    <div className="absolute right-0 top-full mt-1 w-56 px-3 py-2 bg-card border border-border rounded-lg text-xs text-foreground shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Connect all selected social accounts before publishing. <a href="/o-auth-integration-setup" className="text-violet-400 underline">Link accounts</a>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Close button */}
            <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="relative flex-1 overflow-y-auto p-6 space-y-6">
          {/* Regenerating overlay */}
          {isRegenerating && (
            <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3 rounded-b-2xl">
              <Icon name="Loader2" size={28} className="animate-spin text-violet-400" />
              <p className="text-sm font-medium text-foreground">Regenerating content...</p>
              <p className="text-xs text-muted-foreground">This may take a few seconds</p>
            </div>
          )}

          {selectedPlatforms?.linkedin && (
            <PlatformTab
              platform="linkedin"
              content={linkedinContent}
              onChange={setLinkedinContent}
              uploadedImages={uploadedImages?.linkedin || []}
              imagePreviewUrls={imagePreviewUrls?.linkedin || []}
              tokens={tokens}
              tokenStatus={tokenStatus}
              contentPostId={contentPostId}
              saveContentToDashboard={saveContentToDashboard}
              onImagesUploaded={onImagesUploaded}
              onImagePreviewAdded={onImagePreviewAdded}
              onImageRemoved={onImageRemoved}
              publishing={publishing?.linkedin}
              published={publishedPlatforms?.linkedin}
              publishedPostId={publishedPostIds?.linkedin}
              onPublish={onPublish}
              locked={isLocked}
            />
          )}

          {selectedPlatforms?.twitter && (
            <PlatformTab
              platform="twitter"
              content={twitterContent}
              onChange={setTwitterContent}
              uploadedImages={uploadedImages?.twitter || []}
              imagePreviewUrls={imagePreviewUrls?.twitter || []}
              tokens={tokens}
              tokenStatus={tokenStatus}
              contentPostId={contentPostId}
              saveContentToDashboard={saveContentToDashboard}
              onImagesUploaded={onImagesUploaded}
              onImagePreviewAdded={onImagePreviewAdded}
              onImageRemoved={onImageRemoved}
              publishing={publishing?.twitter}
              published={publishedPlatforms?.twitter}
              publishedPostId={publishedPostIds?.twitter}
              onPublish={onPublish}
              locked={isLocked}
            />
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onConfirm={(scheduledAt, timezone, schedulePlatforms) => {
          setShowScheduleModal(false);
          onSchedule?.(scheduledAt, timezone, schedulePlatforms);
        }}
        platforms={selectedPlatforms}
        disabled={scheduling}
      />
    </div>
  );
};

export default EditPreviewModal;
